import {
  VehiclePhotoAngle,
  VehiclePhotoInfo,
  VehiclePhotoPhase,
} from "../../../system/types/Vehicle";
import { REQUIRED_ANGLES } from "./vehiclePhoto";

// 사진 보기 화면의 화면과 무관한 계산. 화면 컴포넌트는 이 함수들만 부른다.

export interface PhotoSets {
  START: VehiclePhotoInfo[];
  END: VehiclePhotoInfo[];
}

/** 비교 화면의 탭: 앞/뒤/좌/우 + 추가 */
export type ViewerTab = VehiclePhotoAngle;
export const VIEWER_TABS: ViewerTab[] = [...REQUIRED_ANGLES, "ETC"];

/** 크게 보기 화면이 보여주는 대상 */
export type ViewerTarget =
  | { kind: "angle"; angle: VehiclePhotoAngle; phase: VehiclePhotoPhase }
  | { kind: "etc"; index: number };

export interface EtcItem {
  phase: VehiclePhotoPhase;
  photo: VehiclePhotoInfo;
}

export function photoAt(
  sets: PhotoSets,
  angle: VehiclePhotoAngle,
  phase: VehiclePhotoPhase
): VehiclePhotoInfo | undefined {
  return sets[phase].find((photo) => photo.angle === angle);
}

/** 추가 사진은 시작 → 종료 순, 같은 단계 안에서는 서버가 준 순서(등록 순) */
export function etcItems(sets: PhotoSets): EtcItem[] {
  return (["START", "END"] as VehiclePhotoPhase[]).flatMap((phase) =>
    sets[phase]
      .filter((photo) => photo.angle === "ETC")
      .map((photo) => ({ phase, photo }))
  );
}

/** 탭 점 색: 시작·종료 둘 다 있음 / 하나만 있음 / 없음 */
export function tabStatus(sets: PhotoSets, tab: ViewerTab): "both" | "one" | "none" {
  if (tab === "ETC") return etcItems(sets).length > 0 ? "both" : "none";
  const count = [photoAt(sets, tab, "START"), photoAt(sets, tab, "END")].filter(Boolean).length;
  return count === 2 ? "both" : count === 1 ? "one" : "none";
}

export function stepTab(tab: ViewerTab, delta: number): ViewerTab {
  const index = VIEWER_TABS.indexOf(tab);
  const length = VIEWER_TABS.length;
  return VIEWER_TABS[(index + delta + length) % length];
}

/** 크게 보기에서 좌우 이동. 방향 사진은 앞→뒤→좌→우를 돌고, 추가 사진은 추가 사진끼리 돈다. */
export function stepTarget(target: ViewerTarget, delta: number, sets: PhotoSets): ViewerTarget {
  if (target.kind === "angle") {
    const index = REQUIRED_ANGLES.indexOf(target.angle);
    const length = REQUIRED_ANGLES.length;
    return { ...target, angle: REQUIRED_ANGLES[(index + delta + length) % length] };
  }
  const length = etcItems(sets).length;
  if (length === 0) return target;
  return { kind: "etc", index: (target.index + delta + length) % length };
}

/** 같은 방향의 시작 ↔ 종료 전환. 추가 사진은 짝이 없어 그대로 둔다. */
export function togglePhase(target: ViewerTarget): ViewerTarget {
  if (target.kind !== "angle") return target;
  return { ...target, phase: target.phase === "START" ? "END" : "START" };
}

export function targetPhoto(
  target: ViewerTarget,
  sets: PhotoSets
): { phase: VehiclePhotoPhase; photo?: VehiclePhotoInfo } {
  if (target.kind === "angle") {
    return { phase: target.phase, photo: photoAt(sets, target.angle, target.phase) };
  }
  const item = etcItems(sets)[target.index];
  return { phase: item?.phase ?? "START", photo: item?.photo };
}

